/**
 * Funnel: stages from lookerChartData.funnelStages as rows of bars, each with
 * its value, its share of the first stage and of the stage before it.
 *
 * Rows, not a recharts funnel: an ad funnel falls by orders of magnitude per
 * stage (3.6M impressions → 201 conversions, measured 2026-10-08), which drew
 * every later stage as a hairline with its label over the first one.
 *
 * @module components/looker/charts/FunnelTileChart
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LookerTile } from '@/services/looker/lookerTypes';
import { formatSharePercent, funnelStages, SERIES_COLORS } from '../lookerChartData';

/** A real, non-zero stage stays visible as a sliver even at 0.01% of the first. */
const MIN_BAR_PCT = 0.5;

export const FunnelTileChart: React.FC<{ tile: LookerTile }> = ({ tile }) => {
  const { t } = useTranslation();
  const stages = funnelStages(tile);
  const first = stages[0]?.value ?? 0;
  return (
    <ol className="space-y-3">
      {stages.map((s, i) => {
        const ofFirst = i > 0 ? formatSharePercent(s.value, first) : null;
        const ofPrevious = i > 1 ? formatSharePercent(s.value, stages[i - 1].value) : null;
        const width = first > 0 && s.value > 0 ? Math.max(MIN_BAR_PCT, Math.min(100, (s.value / first) * 100)) : 0;
        return (
          <li key={`${s.name}-${i}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
              <span className="font-medium text-ink-900">{s.name}</span>
              <span className="tabular-nums text-ink-900">{s.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
            </div>
            <div className="mt-1 h-3 w-full rounded-full bg-neutral-100">
              <div className="h-3 rounded-full" style={{ width: `${width}%`, backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length] }} />
            </div>
            {(ofFirst || ofPrevious) && (
              <p className="mt-1 text-xs tabular-nums text-neutral-700">
                {ofFirst && t('looker.funnelOfFirst', { pct: ofFirst })}
                {ofFirst && ofPrevious && ' · '}
                {ofPrevious && t('looker.funnelOfPrevious', { pct: ofPrevious })}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};
